import React, { useState, useEffect, useRef } from "react";
import FolderTree from "./FolderTree";
import FileList from "./FileList";
import FileUploadModal from "./FileUploadModal";
import FolderCreateModal from "./FolderCreateModal";
import FilePreviewModal from "./FilePreviewModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import RenameFileModal from "./RenameFileModal";
import { FolderPlus, Folder as LucideFolder, FilePlus, File as LucideFile, Archive, Download, Users, Link2, Upload } from "lucide-react";
import { getFirestore, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { app } from "../../../firebase";
import JSZip from "jszip";
import MobileFolderView from "./MobileFolderView";
import { useDispatch, useSelector } from "react-redux";
import { subscribeFileStorage, fetchFileStorageOnce, unsubscribeFileStorage } from '../../../store/fileStorageActions';
import { RootState } from '../../../store/store';

const FileStorageMain = ({ user }) => {
  const dispatch = useDispatch();
  const folders = useSelector(state => state.fileStorage.folders);
  const files = useSelector(state => state.fileStorage.files);
  const initialized = useSelector(state => state.fileStorage.initialized);
  // Sort state: { key: 'name' | 'size' | 'date', order: 'asc' | 'desc' }
  const [folderSort, setFolderSort] = useState({ key: 'name', order: 'asc' });
  const [fileSort, setFileSort] = useState({ key: 'name', order: 'asc' });
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef();
  // Search logic: filter folders and files by name
  useEffect(() => {
    if (search.trim() === "") {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    const searchLower = search.toLowerCase();
    const folderMatches = folders.filter(f => f.name && f.name.toLowerCase().includes(searchLower));
    const fileMatches = files.filter(f => f.name && f.name.toLowerCase().includes(searchLower));
    setSearchResults([
      ...folderMatches.map(f => ({ type: "folder", ...f })),
      ...fileMatches.map(f => ({ type: "file", ...f })),
    ]);
    setShowSearchDropdown(true);
  }, [search, folders, files]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [deleteFolderLoading, setDeleteFolderLoading] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  // Delete folder from Firestore and Storage (recursive)
  const handleDeleteFolder = (folder) => {
    setDeleteFolderTarget(folder);
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    setDeleteFolderLoading(true);
    try {
      const storage = getStorage(app);
      // Recursively delete all files in this folder and subfolders
      const deleteFilesRecursively = async (folderId) => {
        // Delete files in this folder
        const folderFiles = files.filter(f => f.folderId === folderId);
        for (const file of folderFiles) {
          const fileRef = ref(storage, `fileStorage/${folderId}/${file.name}`);
          await deleteObject(fileRef).catch(() => {});
          await deleteDoc(doc(db, "fileStorages", file.id)).catch(() => {});
        }
        // Delete subfolders recursively
        const subfolders = folders.filter(f => f.parentId === folderId);
        for (const sub of subfolders) {
          await deleteFilesRecursively(sub.id);
        }
        // Delete folder doc
        await deleteDoc(doc(db, "folderStructures", folderId)).catch(() => {});
      };
      await deleteFilesRecursively(deleteFolderTarget.id);
      setDeleteFolderTarget(null);
      // If the deleted folder was selected, clear selection
      if (selectedFolder === deleteFolderTarget.id) setSelectedFolder(null);
      // No need to call fetchData, Redux will update via onSnapshot
    } catch (e) {
      alert("Failed to delete folder: " + e.message);
    }
    setDeleteFolderLoading(false);
  };

  const db = getFirestore(app);
  // fetchData is now handled by Redux actions

  // On mount: fetch once, then subscribe to real-time updates
  useEffect(() => {
    if (!initialized) {
      dispatch(fetchFileStorageOnce());
    }
    dispatch(subscribeFileStorage());
    return () => {
      unsubscribeFileStorage();
    };
    // eslint-disable-next-line
  }, []);

  // Delete file from storage and firestore
  const handleDeleteFile = async (file) => {
    setDeleteTarget(file);
  };

  const confirmDeleteFile = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const storage = getStorage(app);
      const fileRef = ref(storage, `fileStorage/${deleteTarget.folderId}/${deleteTarget.name}`);
      await deleteObject(fileRef);
      await deleteDoc(doc(db, "fileStorages", deleteTarget.id));
      setDeleteTarget(null);
      // No need to call fetchData, Redux will update via onSnapshot
    } catch (e) {
      alert("Failed to delete file: " + e.message);
    }
    setDeleteLoading(false);
  };

  // Rename file handlers
  const handleRenameFile = (file) => {
    setRenameTarget(file);
  };

  const confirmRenameFile = async (newName) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const storage = getStorage(app);
      const db = getFirestore(app);
      
      // Get the old file reference path
      const oldFileRef = ref(storage, `fileStorage/${renameTarget.folderId}/${renameTarget.name}`);
      const newFileRef = ref(storage, `fileStorage/${renameTarget.folderId}/${newName}`);
      
      // Download the file from old location
      const response = await fetch(renameTarget.downloadUrl);
      const blob = await response.blob();
      
      // Upload to new location
      const { uploadBytes, getDownloadURL } = await import("firebase/storage");
      await uploadBytes(newFileRef, blob);
      const newDownloadUrl = await getDownloadURL(newFileRef);
      
      // Update Firestore document with new name and download URL
      await updateDoc(doc(db, "fileStorages", renameTarget.id), {
        name: newName,
        downloadUrl: newDownloadUrl,
        updatedAt: new Date(),
      });
      
      // Delete old file from storage
      try {
        await deleteObject(oldFileRef);
      } catch (e) {
        console.log("Could not delete old file:", e);
      }
      
      setRenameTarget(null);
      // Redux will update via onSnapshot
    } catch (e) {
      alert("Failed to rename file: " + e.message);
      console.error("Rename error:", e);
    }
    setRenameLoading(false);
  };

  const handlePreviewFile = (file) => setPreviewFile(file);
  const handleDownloadFile = async (file) => {
    setLoading(true);
    try {
      const response = await fetch(file.downloadUrl);
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.name;
      a.click();
    } catch (err) {
      alert("Failed to download file: " + err.message);
    }
    setLoading(false);
  };

  // Download all files in the selected folder and subfolders as a real ZIP using JSZip
  const handleDownloadAllZip = async () => {
    if (!selectedFolder) return;
    setLoading(true);
    try {
      const rootFolder = folders.find(f => f.id === selectedFolder);
      const rootFolderName = rootFolder ? rootFolder.name : "files";
      const zip = new JSZip();

      // Recursively add files to zip with folder structure
      const addFilesToZip = async (folderId, path = "") => {
        const folderFiles = files.filter(f => f.folderId === folderId);
        for (const file of folderFiles) {
          try {
            const response = await fetch(file.downloadUrl);
            const blob = await response.blob();
            zip.file(`${path}${file.name}`, blob);
          } catch (err) {
            zip.file(`${path}${file.name}_ERROR.txt`, `Failed to fetch this file: ${err}`);
          }
        }
        const subfolders = folders.filter(f => f.parentId === folderId);
        for (const sub of subfolders) {
          await addFilesToZip(sub.id, `${path}${sub.name}/`);
        }
      };

      await addFilesToZip(selectedFolder, rootFolderName + "/");
      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `${rootFolderName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      alert("Failed to download files: " + (err.message || err));
    }
    setLoading(false);
  };

  // UI/UX: close modals and refresh after upload/folder create
  // When uploading or creating, store the current date/time
  const handleUploaded = () => {
    setShowUpload(false);
    // Redux will update via onSnapshot
  };
  const handleCreated = (newFolderId) => {
    setShowCreateFolder(false);
    setSelectedFolder(newFolderId);
    // Redux will update via onSnapshot
  };

  // Sorting helpers
  const sortArray = (arr, key, order) => {
    const sorted = [...arr].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'size') {
        aVal = Number(a.size) || 0;
        bVal = Number(b.size) || 0;
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      } else if (key === 'date') {
        aVal = a.updatedAt || a.modifiedAt || a.createdAt || new Date().toISOString();
        bVal = b.updatedAt || b.modifiedAt || b.createdAt || new Date().toISOString();
      } else {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  // Sorted folders and files for display
  const sortedFolders = sortArray(folders, folderSort.key, folderSort.order);
  const sortedFiles = sortArray(files.filter(f => f.folderId === selectedFolder), fileSort.key, fileSort.order);

  // Mobile view detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sort bar component
  const SortBar = ({ sort, setSort, type }) => (
    <div className="flex gap-2 items-center mb-2">
      <span className="text-xs text-gray-500">Sort by:</span>
      {[['name', 'Name'], ['size', 'Size'], ['date', 'Date Modified']].map(([key, label]) => (
        <button
          key={key}
          className={`px-2 py-1 text-xs rounded ${sort.key === key ? 'bg-blue-100 text-blue-700 font-bold' : 'bg-gray-100 text-gray-600'} hover:bg-blue-200 transition`}
          onClick={() => setSort(s => ({ key, order: s.key === key && s.order === 'asc' ? 'desc' : 'asc' }))}
        >
          {label} {sort.key === key ? (sort.order === 'asc' ? '▲' : '▼') : ''}
        </button>
      ))}
    </div>
  );



      const publicLink = `${window.location.origin}/public-folder-upload`;
      const [copied, setCopied] = useState(false);
      const handleCopy = () => {
        navigator.clipboard.writeText(publicLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      };

  return (
    <>
      {/* Search bar and public link copy button side by side */}
      <div className="w-full flex flex-col md:flex-row items-center justify-center gap-4 mb-6 mt-2 mr-2" style={{zIndex: 20}}>
        {/* Search Bar */}
        <div className="relative flex-1 w-full md:w-auto" ref={searchRef}>
          <input
            type="text"
            className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-200"
            placeholder="Search folders and files..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => search && setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchResults.length > 0 && (
            <ul className="absolute left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-64 overflow-auto z-50">
              {searchResults.map(result => (
                <li
                  key={result.type + result.id}
                  className="flex items-center px-4 py-2 cursor-pointer hover:bg-blue-50"
                  onClick={() => {
                    setShowSearchDropdown(false);
                    setSearch("");
                    if (result.type === "folder") {
                      setSelectedFolder(result.id);
                    } else if (result.type === "file") {
                      setSelectedFolder(result.folderId);
                      // Do NOT open preview automatically
                    }
                  }}
                >
                  <span className="mr-2">
                    {result.type === "folder" ? "📁" : "📄"}
                  </span>
                  <span className="truncate font-medium text-gray-800">{result.name}</span>
                  <span className="ml-auto text-xs text-gray-400">{result.type}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Public Link Copy Button */}
  <div className="flex gap-3 items-center bg-white/80 rounded-xl px-4 py-2 shadow border border-blue-100 relative max-w-full md:max-w-none" style={{ maxWidth: '92vw' }}>
          <span title="Public Upload" className="text-[#0071bd]"> <Users size={22} /> </span>
          <span title="Upload" className="text-green-600"> <Upload size={22} /> </span>
          <span className="text-xs text-gray-700 font-mono select-all max-w-[160px] truncate" title={publicLink}>{publicLink}</span>
          <div className="relative flex items-center">
            <button
              onClick={handleCopy}
              className={`rounded-full p-2 transition duration-200 focus:outline-none ${copied ? 'bg-green-100 scale-110' : 'hover:bg-gray-200'}`}
              onMouseEnter={e => {
                const tooltip = e.currentTarget.nextSibling;
                if (tooltip) tooltip.style.opacity = 1;
              }}
              onMouseLeave={e => {
                const tooltip = e.currentTarget.nextSibling;
                if (tooltip) tooltip.style.opacity = 0;
              }}
              aria-label={copied ? 'Copied!' : 'Copy Link'}
            >
              <Link2 size={22} className={copied ? 'text-green-600 animate-pulse' : 'text-gray-700'} />
            </button>
            {/* Tooltip */}
            <span
              className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 rounded bg-gray-800 text-white text-xs whitespace-nowrap pointer-events-none opacity-0 transition-opacity duration-200 z-50 shadow-lg"
              style={{
                opacity: 0,
                minWidth: '70px',
              }}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </span>
          </div>
        </div>
      </div>
      {/* Mobile: show selected folder as overlay with back button */}
      {isMobile && selectedFolder ? (
        <MobileFolderView
          folder={folders.find(f => f.id === selectedFolder)}
          files={sortedFiles}
          onBack={() => setSelectedFolder(null)}
          onPreview={handlePreviewFile}
          onDelete={handleDeleteFile}
          onDownload={handleDownloadFile}
          onRename={handleRenameFile}
          loading={loading}
          onUploaded={typeof handleUploaded === 'function' ? handleUploaded : () => {}}
        />
      ) : (
        <>
          <div
            className="flex flex-col md:flex-row w-full h-full bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-2xl p-4 min-h-[80vh] mr-2"
            onClick={e => {
              if (e.target === e.currentTarget) setSelectedFolder(null);
            }}
            style={{ position: 'relative' }}
          >
            {/* Left: Folder Tree */}
            <div
              className="w-full md:w-2/3 pr-0 md:pr-6 mb-4 md:mb-0 "
              style={{ zIndex: 2 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <LucideFolder size={28} strokeWidth={1.5} className="text-yellow-500" />
                  <h2 className="text-xl font-extrabold tracking-tight text-blue-700">Folders</h2>
                </div>
                <button
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 bg-[#0071bd] transition text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300"
                  onClick={() => setShowCreateFolder(true)}
                  title="Create Folder"
                >
                  <FolderPlus size={20} strokeWidth={2} className="text-white p-1" />
                  <span className="hidden md:inline">New Folder</span>
                </button>
              </div>
              <SortBar sort={folderSort} setSort={setFolderSort} type="folder" />
              <FolderTree
                folders={sortedFolders}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                onDeleteFolder={handleDeleteFolder}
                files={files}
              />
            </div>
            {/* Divider for large screens */}
            <div className="hidden md:flex flex-col justify-center items-center mx-2">
              <div className="my-6 w-0.5 h-5/6 bg-gray-300 rounded-full" style={{ minHeight: '120px' }}></div>
            </div>
            {/* Right: File List or Empty State */}
            <div
              className="w-full md:w-2/3 pl-2 md:pl-6 flex flex-col"
              style={{ zIndex: 1 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <LucideFile size={26} strokeWidth={1.5} className="text-blue-500" />
                  <h2 className="text-xl font-extrabold tracking-tight text-blue-700">Files</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300 bg-[#0071bd]"
                    onClick={() => setShowUpload(true)}
                    disabled={!selectedFolder}
                    title="Add File"
                  >
                    <FilePlus size={20} strokeWidth={2} className="text-white" />
                    <span className="hidden md:inline">Add File</span>
                  </button>
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition text-base font-semibold focus:outline-none focus:ring-2 focus:ring-green-300"
                    onClick={handleDownloadAllZip}
                    disabled={!selectedFolder || loading}
                    title="Download All as ZIP"
                  >
                    <Archive size={20} strokeWidth={2} className="text-white" />
                    <Download size={18} strokeWidth={2} className="text-white" />
                    <span className="hidden md:inline">ZIP</span>
                  </button>
                </div>
              </div>
              <SortBar sort={fileSort} setSort={setFileSort} type="file" />
              {/* File List or Empty State */}
              {selectedFolder ? (
                <>
                  <FileList
                    files={sortedFiles}
                    onPreview={handlePreviewFile}
                    onDelete={handleDeleteFile}
                    onDownload={handleDownloadFile}
                    onRename={handleRenameFile}
                  />
                  {loading && (
                    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                      <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
                        <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                        <div className="text-blue-700 font-semibold">Downloading...</div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center text-gray-400">
                  <svg width="80" height="80" fill="none" viewBox="0 0 24 24" className="mx-auto mb-4"><path stroke="#60a5fa" strokeWidth="1.5" d="M3.75 6.75A2.25 2.25 0 0 1 6 4.5h3.382a2.25 2.25 0 0 1 1.59.659l1.829 1.828a2.25 2.25 0 0 0 1.59.659H18a2.25 2.25 0 0 1 2.25 2.25v7.5A2.25 2.25 0 0 1 18 19.5H6A2.25 2.25 0 0 1 3.75 17.25v-10.5Z"/><path stroke="#60a5fa" strokeWidth="1.5" d="M8.25 12.75h7.5m-7.5 3h4.5"/></svg>
                  <div className="text-lg font-semibold mb-2">No folder selected</div>
                  <div className="mb-2">Select a folder from the left to view or upload files.</div>
                  <div className="text-sm text-gray-300">Tip: Create a folder to get started!</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {showUpload && (
        <FileUploadModal
          folderId={selectedFolder}
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}
      {showCreateFolder && (
        <FolderCreateModal
          parentId={selectedFolder ? selectedFolder : null}
          onClose={() => setShowCreateFolder(false)}
          onCreated={handleCreated}
        />
      )}
      {/* Folder Delete Modal */}
      {deleteFolderTarget && (
        <ConfirmDeleteModal
          open={!!deleteFolderTarget}
          onClose={() => setDeleteFolderTarget(null)}
          onConfirm={confirmDeleteFolder}
          loading={deleteFolderLoading}
          target={deleteFolderTarget}
          isFolder={true}
        />
      )}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDeleteFile}
          loading={deleteLoading}
          target={deleteTarget}
        />
      )}
      {renameTarget && (
        <RenameFileModal
          open={!!renameTarget}
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null);
          }}
          file={renameTarget}
          onConfirm={confirmRenameFile}
          loading={renameLoading}
        />
      )}
      {/* Global loader for rename operation */}
      {renameLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[9999]">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
            <div className="text-blue-700 font-semibold">Renaming file...</div>
          </div>
        </div>
      )}

        {/* Modals */}
        {showUpload && (
          <FileUploadModal
            folderId={selectedFolder}
            onClose={() => setShowUpload(false)}
            onUploaded={handleUploaded}
          />
        )}
        {showCreateFolder && (
          <FolderCreateModal
            parentId={selectedFolder ? selectedFolder : null}
            onClose={() => setShowCreateFolder(false)}
            onCreated={handleCreated}
          />
        )}
        {/* Folder Delete Modal */}
        {deleteFolderTarget && (
          <ConfirmDeleteModal
            open={!!deleteFolderTarget}
            onClose={() => setDeleteFolderTarget(null)}
            onConfirm={confirmDeleteFolder}
            loading={deleteFolderLoading}
            target={deleteFolderTarget}
            isFolder={true}
          />
        )}
        {previewFile && (
          <FilePreviewModal
            file={previewFile}
            onClose={() => setPreviewFile(null)}
          />
        )}
        {deleteTarget && (
          <ConfirmDeleteModal
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={confirmDeleteFile}
            loading={deleteLoading}
            target={deleteTarget}
          />
        )}
    </>
  );
};

export default FileStorageMain;
